import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { from as observableFrom, Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { HttpStatusCode } from '../shared/constants/http-status-code.enum';
import { FetchStrategy } from '../shared/instrumentation/instrumented-http.service';
import { SERVICE_WORKER_ENABLED } from './service-worker-enabled.injection-token';
import { serviceWorkerFetchStrategyHeader } from './service-worker-fetch-strategy-header.constant';
import { ServiceWorkerState, ServiceWorkerStateService } from './service-worker-state.service';
import { SERVICE_WORKER_USER_ID_FACTORY } from './service-worker-user-id.injection-token';

export const serviceWorkerUserCacheIdHeader = 'X-SW-OHP-UserCacheId';

@Injectable()
export class ServiceWorkerCacheHeaderInterceptor implements HttpInterceptor {
    constructor(
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) private readonly serviceWorkerUserIdFactory: () => Promise<string>,
        private readonly serviceWorkerStateService: ServiceWorkerStateService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler
    ) {
    }

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.serviceWorkerEnabled) {
            return next.handle(request);
        }

        // In the case where the service worker isn't controlling the page, we don't want to append the custom
        // cache ID header because then API calls may refuse to accept the request sent due to
        // 'Access-Control-Allow-Headers'
        if (this.serviceWorkerStateService.currentState !== ServiceWorkerState.Controlled) {
            return next.handle(request);
        }

        // Network Only requests don't need to block on obtaining the `serviceWorkerUserId`
        const cacheStrategy = request.headers.get(serviceWorkerFetchStrategyHeader);

        if (!cacheStrategy) {
            return next.handle(request);
        }

        if (cacheStrategy === FetchStrategy.NetworkOnly) {
            // Remove the cache strategy header, as it is not required for NetworkOnly requests. They should pass through
            // using browser default behavior.
            return next.handle(request.clone({
                headers: request.headers.delete(serviceWorkerFetchStrategyHeader)
            }));
        } else if (request.reportProgress) {
            // If it's not a NetworkOnly strategy, but request wants to report HTTP progress, throw an exception.
            // See: https://github.com/w3c/ServiceWorker/issues/1141 for details
            // TODO: revisit this when Fetch API supports HTTP progress callbacks yet.

            const errorMessage = 'Cannot report progress for non-NetworkOnly requests';

            throw new HttpErrorResponse({
                error: errorMessage,
                statusText: errorMessage,
                status: HttpStatusCode.NotImplemented,
                url: request.urlWithParams
            });
        }

        return observableFrom(this.serviceWorkerUserIdFactory()).pipe(
            // Append a unique identifier for the service worker to key caches on (so user caches don't collide)
            // The custom header will be removed by the service worker before sending requests out to the API
            map(serviceWorkerUserId => request.clone({
                headers: request.headers.append(serviceWorkerUserCacheIdHeader, serviceWorkerUserId)
            })),
            catchError(error => {
                // If the user cache ID fails to load, we log the error, and set the request to use `network-only`.
                this.globalErrorHandler.handleError(error, ErrorId.j9XG4);

                if (cacheStrategy === FetchStrategy.CacheOnly) {
                    return observableThrowError(new HttpErrorResponse({
                        error: 'Could not send CacheOnly request without a service worker user cache ID',
                        status: HttpStatusCode.Cancelled,
                        statusText: 'CacheOnly request cancelled'
                    }));
                } else {
                    // This will be a NetworkFirst call. Convert it to network only, which doesn't require a
                    // a user cache ID.
                    return observableOf(request.clone({
                        headers: request.headers.delete(serviceWorkerFetchStrategyHeader)
                    }));
                }
            }),
            switchMap(newRequest => next.handle(newRequest))
        );
    }
}
